import React, { useState, useEffect } from 'react';
import {
  Box, Card, CardContent, Chip, CircularProgress,
  IconButton, LinearProgress, Tooltip, Typography,
} from '@mui/material';
import { AutoAwesome, School, TrendingUp } from '@mui/icons-material';
import { talentApi } from '../services/hrmsApi';
import { useChatContext } from '../context/ChatContext';

const PRIORITY = {
  high:   { bg: '#FFEBEE', color: '#C62828', label: 'High' },
  medium: { bg: '#FFFDE7', color: '#F57F17', label: 'Medium' },
  low:    { bg: '#E3F2FD', color: '#0D47A1', label: 'Low' },
};

const DEPT_COLOR = (dept) => {
  const map = { IT: '#1565C0', Finance: '#00897B', Operations: '#6A1B9A', Sales: '#E65100', HR: '#AD1457' };
  return map[dept] || '#546E7A';
};

const STUB = {
  summary: '14 employees across 3 departments have been identified for targeted upskilling based on current skill gap analysis.',
  recommendations: [
    { id: 'r1', courseTitle: 'AWS Cloud Practitioner',     category: 'Cloud Management', targetDepartment: 'IT',         targetCount: 8,  skillAddressed: 'Cloud Architecture',  currentProficiency: 3.5, requiredProficiency: 7.0, priority: 'high',   estimatedDuration: '40 hrs', estimatedImpact: 'Closes 3 of 5 critical IT skill gaps' },
    { id: 'r2', courseTitle: 'Generative AI for Business', category: 'AI Tools',         targetDepartment: 'Operations', targetCount: 12, skillAddressed: 'AI Literacy',         currentProficiency: 2.0, requiredProficiency: 6.0, priority: 'high',   estimatedDuration: '16 hrs', estimatedImpact: 'Addresses top training demand signal across all departments' },
    { id: 'r3', courseTitle: 'Cybersecurity Fundamentals', category: 'Security',         targetDepartment: 'IT',         targetCount: 3,  skillAddressed: 'Cybersecurity',       currentProficiency: 4.0, requiredProficiency: 8.0, priority: 'high',   estimatedDuration: '32 hrs', estimatedImpact: 'Compliance requirement — Q3 deadline' },
    { id: 'r4', courseTitle: 'Python for Data Analysis',   category: 'Data Skills',      targetDepartment: 'Finance',    targetCount: 5,  skillAddressed: 'Data Analytics',      currentProficiency: 4.5, requiredProficiency: 7.5, priority: 'medium', estimatedDuration: '24 hrs', estimatedImpact: 'Reduces manual reporting time by est. 30%' },
    { id: 'r5', courseTitle: 'Leadership Essentials',      category: 'Leadership',       targetDepartment: 'Sales',      targetCount: 6,  skillAddressed: 'People Management',   currentProficiency: 5.0, requiredProficiency: 8.0, priority: 'medium', estimatedDuration: '20 hrs', estimatedImpact: 'Supports 6 promotion-ready candidates in 9-box High Potential tier' },
    { id: 'r6', courseTitle: 'Presentation Skills',        category: 'Soft Skills',      targetDepartment: 'HR',         targetCount: 9,  skillAddressed: 'Communication',       currentProficiency: 5.5, requiredProficiency: 7.0, priority: 'low',    estimatedDuration: '8 hrs',  estimatedImpact: 'Supports cross-department collaboration initiatives' },
  ],
};

const FILTERS = ['All', 'High', 'Medium', 'Low'];

function ProficiencyBar({ current, required }) {
  const pct     = Math.min((current / required) * 100, 100);
  const isMet   = current >= required;
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
      <Box sx={{ flex: 1 }}>
        <LinearProgress
          variant="determinate"
          value={pct}
          sx={{
            height: 5, borderRadius: 3,
            bgcolor: 'action.hover',
            '& .MuiLinearProgress-bar': { bgcolor: isMet ? 'success.main' : '#F57F17', borderRadius: 3 },
          }}
        />
      </Box>
      <Typography variant="caption" color="text.secondary" sx={{ whiteSpace: 'nowrap', fontSize: '0.68rem' }}>
        {current} / {required}
      </Typography>
    </Box>
  );
}

function RecommendationRow({ rec, onAskAI }) {
  const ps   = PRIORITY[rec.priority] || PRIORITY.medium;
  const dc   = DEPT_COLOR(rec.targetDepartment);
  return (
    <Box sx={{
      display: 'flex', alignItems: 'flex-start', gap: 1.5, py: 1.5,
      borderBottom: '1px solid', borderColor: 'divider',
      '&:last-child': { borderBottom: 'none' },
    }}>
      {/* Priority dot + course info */}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Chip label={ps.label} size="small"
            sx={{ bgcolor: ps.bg, color: ps.color, fontWeight: 700, fontSize: '0.68rem', height: 20, '& .MuiChip-label': { px: 0.75 } }} />
          <Typography variant="body2" fontWeight={700} noWrap sx={{ flex: '1 1 180px' }}>
            {rec.courseTitle}
          </Typography>
          <Chip label={rec.targetDepartment} size="small"
            sx={{ bgcolor: `${dc}12`, color: dc, fontWeight: 600, fontSize: '0.68rem', height: 20, '& .MuiChip-label': { px: 0.75 } }} />
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5, flexWrap: 'wrap' }}>
          <Typography variant="caption" color="text.secondary">
            Skill: <strong>{rec.skillAddressed}</strong>
          </Typography>
          <Typography variant="caption" color="text.disabled">·</Typography>
          <Typography variant="caption" color="text.secondary">
            {rec.targetCount} employees
          </Typography>
          <Typography variant="caption" color="text.disabled">·</Typography>
          <Typography variant="caption" color="text.secondary">
            {rec.estimatedDuration}
          </Typography>
        </Box>

        <ProficiencyBar current={rec.currentProficiency} required={rec.requiredProficiency} />

        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.4, fontStyle: 'italic', fontSize: '0.7rem' }}>
          {rec.estimatedImpact}
        </Typography>
      </Box>

      {/* Ask AI button */}
      <Tooltip title="Ask AI about this recommendation">
        <IconButton size="small" onClick={() => onAskAI(rec)}
          sx={{ mt: 0.25, flexShrink: 0, color: 'primary.main', opacity: 0.5, '&:hover': { opacity: 1 }, p: 0.5 }}>
          <AutoAwesome sx={{ fontSize: 15 }} />
        </IconButton>
      </Tooltip>
    </Box>
  );
}

export default function TrainingRecommendationsPanel() {
  const { openWithMessage }          = useChatContext();
  const [data,    setData]           = useState(null);
  const [loading, setLoading]        = useState(true);
  const [activeFilter, setFilter]    = useState('All');

  useEffect(() => {
    talentApi.getRecommendations()
      .then(setData)
      .catch(() => setData(STUB))
      .finally(() => setLoading(false));
  }, []);

  const recommendations = (data?.recommendations || []).filter(
    (r) => activeFilter === 'All' || r.priority === activeFilter.toLowerCase()
  );

  const handleAskAI = (rec) => {
    openWithMessage(
      `We have a training recommendation: "${rec.courseTitle}" for ${rec.targetCount} employees in ${rec.targetDepartment} to address the "${rec.skillAddressed}" skill gap (current proficiency ${rec.currentProficiency}/10, required ${rec.requiredProficiency}/10). Priority: ${rec.priority}. Estimated impact: ${rec.estimatedImpact}. Is this the right approach, and are there faster alternatives?`
    );
  };

  return (
    <Card>
      <CardContent>
        {/* Header */}
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', mb: 1.5, gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: '8px', background: '#E3F2FD', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <School sx={{ fontSize: 18, color: '#1565C0' }} />
            </Box>
            <Box>
              <Typography variant="subtitle1" fontWeight={700} lineHeight={1.2}>
                AI Training Recommendations
              </Typography>
              {loading ? null : (
                <Typography variant="caption" color="text.secondary">
                  {data?.summary}
                </Typography>
              )}
            </Box>
          </Box>

          {/* Priority filter chips */}
          <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap' }}>
            {FILTERS.map((f) => {
              const ps  = f === 'All' ? null : PRIORITY[f.toLowerCase()];
              const cnt = f === 'All'
                ? (data?.recommendations || []).length
                : (data?.recommendations || []).filter(r => r.priority === f.toLowerCase()).length;
              return (
                <Chip
                  key={f}
                  label={`${f}${data ? ` (${cnt})` : ''}`}
                  size="small"
                  onClick={() => setFilter(f)}
                  sx={{
                    fontWeight: 600, fontSize: '0.72rem', cursor: 'pointer',
                    bgcolor: activeFilter === f ? (ps?.bg || 'primary.main') : 'action.hover',
                    color:   activeFilter === f ? (ps?.color || '#fff') : 'text.secondary',
                    '&:hover': { opacity: 0.85 },
                  }}
                />
              );
            })}
          </Box>
        </Box>

        {/* List */}
        {loading ? (
          <Box sx={{ py: 3, display: 'flex', justifyContent: 'center' }}>
            <CircularProgress size={24} />
          </Box>
        ) : recommendations.length === 0 ? (
          <Typography variant="body2" color="text.secondary" sx={{ py: 2, textAlign: 'center' }}>
            No recommendations match this filter.
          </Typography>
        ) : (
          <Box>
            {recommendations.map((rec) => (
              <RecommendationRow key={rec.id} rec={rec} onAskAI={handleAskAI} />
            ))}
          </Box>
        )}

        {/* Footer insight */}
        {!loading && recommendations.length > 0 && (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mt: 1.5, pt: 1.5, borderTop: '1px solid', borderColor: 'divider' }}>
            <TrendingUp sx={{ fontSize: 14, color: 'primary.main' }} />
            <Typography variant="caption" color="text.secondary">
              Recommendations are ranked by skill gap severity and organisational impact.
            </Typography>
            <Tooltip title="Ask AI to prioritise the full training plan">
              <IconButton size="small"
                onClick={() => openWithMessage(`Based on our current skill gap data, there are ${data?.recommendations?.length || 0} training recommendations. How should we sequence these to maximise ROI within a limited training budget?`)}
                sx={{ ml: 'auto', color: 'primary.main', opacity: 0.55, '&:hover': { opacity: 1 }, p: 0.4 }}>
                <AutoAwesome sx={{ fontSize: 14 }} />
              </IconButton>
            </Tooltip>
          </Box>
        )}
      </CardContent>
    </Card>
  );
}
